import { tableColumnWidths } from '@/features/shared/components/tableSystem/tableColumnWidths';
import { getDetailLabel } from '@/shared/copy/details';
import type { GridColDef } from '@/ui/primitives';

export type DeferredSkeletonRow = { id: string };

export type DeferredTablePanelSkeletonVariant =
  | 'default'
  | 'pipelines'
  | 'executionRuns'
  | 'resultsFiles'
  | 'uploads'
  | 'suites'
  | 'tests'
  | 'suiteRuns'
  | 'testRuns'
  | 'builds'
  | 'buildsNoPipeline'
  | 'stages'
  | 'stageExecutions';

export const deferredTableColumnsByVariant: Record<
  DeferredTablePanelSkeletonVariant,
  Array<GridColDef<DeferredSkeletonRow>>
> = {
  default: [
    { field: 'col-1', headerName: '', flex: 1.3 },
    { field: 'col-2', headerName: '', flex: 0.9 },
    { field: 'col-3', headerName: '', flex: 0.9 },
    { field: 'col-4', headerName: '', flex: 0.9 },
    { field: 'col-5', headerName: '', flex: 0.3 },
  ],
  pipelines: [
    {
      field: 'name',
      headerName: getDetailLabel('pipeline'),
      flex: tableColumnWidths.pipelines.name.flex,
      cellClassName: 'table-name-cell',
    },
    {
      field: 'latestRunStatus',
      headerName: 'Latest Status',
      flex: tableColumnWidths.pipelines.latestStatus.flex,
      cellClassName: 'table-status-meta-cell',
    },
    {
      field: 'stageStatusCounts',
      headerName: 'Latest Stages',
      flex: tableColumnWidths.pipelines.latestStages.flex,
      cellClassName: 'table-breakdown-cell',
    },
    {
      field: 'latestJobStatusCounts',
      headerName: 'Latest Jobs',
      flex: tableColumnWidths.pipelines.latestJobs.flex,
      cellClassName: 'table-breakdown-cell',
    },
    {
      field: 'metricsSummary',
      headerName: getDetailLabel('metrics'),
      flex: tableColumnWidths.pipelines.metrics.flex,
    },
    {
      field: 'actions',
      headerName: '',
      flex: tableColumnWidths.pipelines.actions.flex,
      align: 'center',
      headerAlign: 'center',
    },
  ],
  executionRuns: [
    {
      field: 'name',
      headerName: getDetailLabel('run'),
      flex: tableColumnWidths.executionRuns.name.flex,
      cellClassName: 'table-name-cell',
    },
    {
      field: 'status',
      headerName: getDetailLabel('status'),
      flex: tableColumnWidths.executionRuns.status.flex,
      cellClassName: 'table-status-meta-cell',
    },
    {
      field: 'suites',
      headerName: getDetailLabel('suites'),
      flex: tableColumnWidths.executionRuns.suites.flex,
      cellClassName: 'table-breakdown-cell',
    },
    {
      field: 'tests',
      headerName: getDetailLabel('tests'),
      flex: tableColumnWidths.executionRuns.tests.flex,
      cellClassName: 'table-breakdown-cell',
    },
    {
      field: 'details',
      headerName: getDetailLabel('details'),
      flex: tableColumnWidths.executionRuns.details.flex,
      cellClassName: 'table-meta-details-cell',
    },
    {
      field: 'actions',
      headerName: '',
      flex: tableColumnWidths.executionRuns.actions.flex,
      align: 'center',
      headerAlign: 'center',
    },
  ],
  resultsFiles: [
    {
      field: 'fileName',
      headerName: 'File',
      flex: tableColumnWidths.resultsFiles.file.flex,
      cellClassName: 'table-name-cell',
    },
    {
      field: 'executionRunPassed',
      headerName: 'Execution Runs',
      flex: tableColumnWidths.resultsFiles.executionRuns.flex,
      cellClassName: 'table-breakdown-cell',
    },
    {
      field: 'suiteRunPassed',
      headerName: getDetailLabel('suites'),
      flex: tableColumnWidths.resultsFiles.suites.flex,
      cellClassName: 'table-breakdown-cell',
    },
    {
      field: 'testRunPassed',
      headerName: getDetailLabel('tests'),
      flex: tableColumnWidths.resultsFiles.tests.flex,
      cellClassName: 'table-breakdown-cell',
    },
    {
      field: 'details',
      headerName: getDetailLabel('details'),
      flex: tableColumnWidths.resultsFiles.details.flex,
      cellClassName: 'table-meta-details-cell',
    },
    {
      field: 'actions',
      headerName: '',
      flex: tableColumnWidths.resultsFiles.actions.flex,
      align: 'center',
      headerAlign: 'center',
    },
  ],
  uploads: [
    {
      field: 'id',
      headerName: 'Upload',
      flex: tableColumnWidths.uploads.id.flex,
      cellClassName: 'table-name-cell',
    },
    {
      field: 'status',
      headerName: getDetailLabel('status'),
      flex: tableColumnWidths.uploads.status.flex,
      cellClassName: 'table-status-meta-cell',
    },
    {
      field: 'error',
      headerName: 'Error',
      flex: tableColumnWidths.uploads.error.flex,
      cellClassName: 'table-meta-details-cell',
    },
    {
      field: 'actions',
      headerName: '',
      flex: tableColumnWidths.uploads.actions.flex,
      align: 'center',
      headerAlign: 'center',
    },
  ],
  suites: [
    {
      field: 'name',
      headerName: 'Suite',
      flex: tableColumnWidths.suites.name.flex,
      cellClassName: 'table-name-cell',
    },
    {
      field: 'latestRunStatus',
      headerName: 'Latest Status',
      flex: tableColumnWidths.suites.latestStatus.flex,
      cellClassName: 'table-status-meta-cell',
    },
    {
      field: 'tests',
      headerName: getDetailLabel('tests'),
      flex: tableColumnWidths.suites.latestTests.flex,
      cellClassName: 'table-breakdown-cell',
    },
    {
      field: 'metricsSummary',
      headerName: getDetailLabel('metrics'),
      flex: tableColumnWidths.suites.metrics.flex,
    },
    {
      field: 'actions',
      headerName: '',
      flex: tableColumnWidths.suites.actions.flex,
      align: 'center',
      headerAlign: 'center',
    },
  ],
  tests: [
    {
      field: 'name',
      headerName: 'Test',
      flex: tableColumnWidths.tests.name.flex,
      cellClassName: 'table-name-cell',
    },
    {
      field: 'latestRunStatus',
      headerName: 'Latest Status',
      flex: tableColumnWidths.tests.latestStatus.flex,
      cellClassName: 'table-status-meta-cell',
    },
    {
      field: 'executionStatusCounts',
      headerName: 'Runs',
      flex: tableColumnWidths.tests.executions.flex,
      cellClassName: 'table-breakdown-cell',
    },
    {
      field: 'metricsSummary',
      headerName: getDetailLabel('metrics'),
      flex: tableColumnWidths.tests.metrics.flex,
      cellClassName: 'table-breakdown-cell',
    },
    {
      field: 'actions',
      headerName: '',
      flex: tableColumnWidths.tests.actions.flex,
      align: 'center',
      headerAlign: 'center',
    },
  ],
  suiteRuns: [
    {
      field: 'name',
      headerName: 'Suite',
      flex: tableColumnWidths.suiteRuns.name.flex,
      cellClassName: 'table-name-cell',
    },
    {
      field: 'runNumber',
      headerName: getDetailLabel('run'),
      flex: tableColumnWidths.suiteRuns.run.flex,
      cellClassName: 'table-run-link-cell',
    },
    {
      field: 'status',
      headerName: getDetailLabel('status'),
      flex: tableColumnWidths.suiteRuns.status.flex,
      cellClassName: 'table-status-meta-cell',
    },
    {
      field: 'tests',
      headerName: getDetailLabel('tests'),
      flex: tableColumnWidths.suiteRuns.tests.flex,
      cellClassName: 'table-breakdown-cell',
    },
    {
      field: 'output',
      headerName: getDetailLabel('output'),
      flex: tableColumnWidths.suiteRuns.output.flex,
      cellClassName: 'table-meta-details-cell',
    },
    {
      field: 'runId',
      headerName: getDetailLabel('details'),
      flex: tableColumnWidths.suiteRuns.details.flex,
      cellClassName: 'table-meta-details-cell',
    },
    {
      field: 'actions',
      headerName: '',
      flex: tableColumnWidths.suiteRuns.actions.flex,
      align: 'center',
      headerAlign: 'center',
    },
  ],
  testRuns: [
    {
      field: 'name',
      headerName: 'Test',
      flex: tableColumnWidths.testRuns.name.flex,
      cellClassName: 'table-name-cell',
    },
    {
      field: 'runNumber',
      headerName: getDetailLabel('run'),
      flex: tableColumnWidths.testRuns.run.flex,
      cellClassName: 'table-run-link-cell',
    },
    {
      field: 'status',
      headerName: getDetailLabel('status'),
      flex: tableColumnWidths.testRuns.status.flex,
      cellClassName: 'table-status-meta-cell',
    },
    {
      field: 'output',
      headerName: getDetailLabel('output'),
      flex: tableColumnWidths.testRuns.output.flex,
      cellClassName: 'table-meta-details-cell',
    },
    {
      field: 'details',
      headerName: getDetailLabel('details'),
      flex: tableColumnWidths.testRuns.details.flex,
      cellClassName: 'table-meta-details-cell',
    },
    {
      field: 'actions',
      headerName: '',
      flex: tableColumnWidths.testRuns.actions.flex,
      align: 'center',
      headerAlign: 'center',
    },
  ],
  builds: [
    {
      field: 'pipelineName',
      headerName: getDetailLabel('pipeline'),
      flex: 1,
      cellClassName: 'table-name-cell',
    },
    {
      field: 'buildKey',
      headerName: getDetailLabel('run'),
      flex: 0.55,
      cellClassName: 'table-run-link-cell',
    },
    {
      field: 'status',
      headerName: getDetailLabel('status'),
      flex: 0.8,
      cellClassName: 'table-status-meta-cell',
    },
    {
      field: 'stageStatusCounts',
      headerName: 'Stages',
      flex: 0.6,
      cellClassName: 'table-breakdown-cell',
    },
    { field: 'actions', headerName: '', flex: 0.18, align: 'right', headerAlign: 'right' },
  ],
  buildsNoPipeline: [
    {
      field: 'buildKey',
      headerName: getDetailLabel('run'),
      flex: 0.75,
      cellClassName: 'table-run-link-cell',
    },
    {
      field: 'status',
      headerName: getDetailLabel('status'),
      flex: 0.75,
      cellClassName: 'table-status-meta-cell',
    },
    {
      field: 'stageStatusCounts',
      headerName: 'Stages',
      flex: 0.6,
      cellClassName: 'table-breakdown-cell',
    },
    { field: 'actions', headerName: '', flex: 0.18, align: 'right', headerAlign: 'right' },
  ],
  stages: [
    {
      field: 'stageName',
      headerName: getDetailLabel('stage'),
      flex: 0.9,
      cellClassName: 'table-name-cell',
    },
    {
      field: 'status',
      headerName: 'Latest Status',
      flex: 0.55,
      cellClassName: 'table-status-meta-cell',
    },
    {
      field: 'executionStatusCounts',
      headerName: 'Runs',
      flex: 0.55,
      cellClassName: 'table-breakdown-cell',
    },
    { field: 'metricsSummary', headerName: getDetailLabel('metrics'), flex: 0.9 },
    { field: 'actions', headerName: '', flex: 0.18, align: 'right', headerAlign: 'right' },
  ],
  stageExecutions: [
    {
      field: 'stageName',
      headerName: getDetailLabel('stage'),
      flex: 1,
      cellClassName: 'table-name-cell',
    },
    {
      field: 'buildKey',
      headerName: getDetailLabel('run'),
      flex: 0.5,
      cellClassName: 'table-run-link-cell',
    },
    {
      field: 'status',
      headerName: getDetailLabel('status'),
      flex: 0.75,
      cellClassName: 'table-status-meta-cell',
    },
    { field: 'actions', headerName: '', flex: 0.1, align: 'right', headerAlign: 'right' },
  ],
};
